import { InteractiveContainer, PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { FC } from "react";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";

type ChannelTileStatusType = "connected" | "verify";

type ChannelTileStatus = {
  type: ChannelTileStatusType;
  text: string;
};

interface ChannelTileProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  isSelected: boolean;
  isConnected: boolean;
  isDisabled?: boolean;
  pricingTooltip?: string;
  status?: ChannelTileStatus;
  onCopyHint?: string;
  onSelect: () => void;
  onClick: () => void;
  onCopy?: () => void;
}

export const ChannelTile: FC<ChannelTileProps> = ({
  title,
  description,
  icon,
  isSelected,
  isConnected,
  isDisabled = false,
  pricingTooltip,
  status,
  onCopyHint,
  onSelect,
  onClick,
  onCopy,
}) => {
  const cxSignupDialog = useCXSignupDialog();
  const { isAnonymous } = useAnonymousSession();

  const { t } = useTranslations();

  return (
    <InteractiveContainer
      isDisabled={isDisabled}
      className={cx(
        "relative flex w-full items-center overflow-hidden rounded-xl border border-solid border-dark-100 transition-colors",
        isSelected && "border-dark-700"
      )}
      onClick={onSelect}
    >
      <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
        {isConnected && onCopy && (
          <Tooltip
            className="p-4"
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
                ref={ref}
                disabled={isDisabled}
                {...props}
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy();
                }}
              >
                <CopyIcon />
              </PlainButton>
            )}
          >
            {onCopyHint ?? ""}
          </Tooltip>
        )}
        <Tooltip
          renderTrigger={({ props, ref }) => (
            <PlainButton
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid border-grey-400 transition-colors hover:border-grey-600"
              ref={ref}
              disabled={isDisabled}
              {...props}
              onClick={(e) => {
                e.stopPropagation();
                if (isAnonymous) {
                  cxSignupDialog?.open();
                  return;
                }
                onClick();
              }}
            >
              {isConnected ? (
                <SettingsIcon />
              ) : pricingTooltip ? (
                <PricingIcon />
              ) : (
                <AddIcon />
              )}
            </PlainButton>
          )}
        >
          {isConnected
            ? t({
                id: "cx-ai-agents-page.channel-settings",
                defaultMessage: "Channel Settings",
              })
            : pricingTooltip ||
              t({
                id: "cx-ai-agents-page.connect-channel",
                defaultMessage: "Connect Channel",
              })}
        </Tooltip>
      </div>
      <div
        className="flex h-full w-17.5 shrink-0 items-center justify-center"
        style={{
          backgroundImage:
            "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
          backgroundSize: "8px 8px",
        }}
      >
        {icon}
      </div>
      <div className="flex min-w-0 flex-col gap-6 px-4 py-6">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="truncate font-secondary text-lg leading-[150%] font-medium text-black">
            {title}
          </span>
          <span className="jugl__break-word line-clamp-2 overflow-hidden font-secondary text-sm leading-5.25 text-[#737B7E]">
            {description}
          </span>
        </div>
        {status && (
          <div
            className={cx(
              "flex h-7 w-max items-center justify-center gap-2 rounded-xl border border-solid px-2 font-secondary text-xs font-medium text-dark-800",
              status.type === "verify"
                ? "border-[#F36021]/30 bg-[#FFF7EC]"
                : "border-secondary-100 bg-secondary-50"
            )}
          >
            {status.type === "verify" && (
              <div className="h-[9px] w-[9px] rounded-full bg-[#F36021]" />
            )}
            {status.text}
          </div>
        )}
      </div>
    </InteractiveContainer>
  );
};
